class ScrollFadeAnimations {
    constructor() {
      this.animatedElements = new Set();
      this.observer = null;
      this.timeouts = new Set();
      this.isDestroyed = false;
      
      // Bind methods to preserve context
      this.handleIntersection = this.handleIntersection.bind(this);
      this.handlePageHide = this.handlePageHide.bind(this);
      this.handleResize = this.handleResize.bind(this);
      
      this.init();
    }
  
    init() {
      // Check for IntersectionObserver support
      if (!('IntersectionObserver' in window)) {
        this.handleUnsupportedBrowser();
        return;
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupObserver());
      } else {
        this.setupObserver();
      }

      // Setup cleanup listeners
      this.setupCleanup();
    }

    setupCleanup() {
      // Clean up on page unload to prevent memory leaks
      window.addEventListener('pagehide', this.handlePageHide);
      window.addEventListener('beforeunload', this.handlePageHide);
      window.addEventListener('resize', this.handleResize);
    }

    handlePageHide() {
      this.destroy();
    }

    handleResize() {
      // Debounce resize events to avoid excessive recalculations
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.checkResponsiveElements();
      }, 150);
    }

    checkResponsiveElements() {
      const elements = document.querySelectorAll('.fade-slide-up-on-scroll[data-responsive], .fade-in-on-scroll[data-responsive]');
      elements.forEach(element => {
        const responsiveBreakpoint = element.dataset.responsive;
        if (responsiveBreakpoint && !this.meetsBreakpoint(responsiveBreakpoint)) {
          // If breakpoint requirement not met, show element immediately
          element.classList.remove('opacity-0');
          if (this.animatedElements.has(element) && this.observer) {
            this.observer.unobserve(element);
          }
        } else if (!this.animatedElements.has(element)) {
          // If breakpoint requirement is met and element hasn't been animated, observe it
          element.classList.add('opacity-0');
          if (this.observer) {
            this.observer.observe(element);
          }
        }
      });
    }
  
    setupObserver() {
      if (this.isDestroyed) return;

      // Respect user's motion preferences
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.handleReducedMotion();
        return;
      }

      try {
        this.observer = new IntersectionObserver(
          this.handleIntersection,
          {
            threshold: 0.1,
            rootMargin: '0px 0px 10% 0px'
          }
        );

        this.observeElements();
      } catch (error) {
        console.warn('ScrollFadeAnimations: Failed to setup observer', error);
        this.handleReducedMotion();
      }
    }
  
    observeElements() {
      if (!this.observer || this.isDestroyed) return;

      const elements = document.querySelectorAll('.fade-slide-up-on-scroll, .fade-in-on-scroll, [data-scroll-animation="fade-slide-up"], [data-scroll-animation="fade-in"]');
      
      if (elements.length === 0) {
        this.destroy();
        return;
      }

      elements.forEach(element => {
        try {
          this.observer.observe(element);
        } catch (error) {
          console.warn('ScrollFadeAnimations: Failed to observe element', error);
        }
      });
    }
  
    handleIntersection(entries) {
      if (this.isDestroyed) return;

      const visible = entries.filter(entry =>
        entry.isIntersecting && !this.animatedElements.has(entry.target)
      );

      visible.forEach((entry, index) => {
        const element = entry.target;
        this.animatedElements.add(element);
        this.observer.unobserve(element);

        if (element.dataset.responsive && !this.meetsBreakpoint(element.dataset.responsive)) {
          element.classList.remove('opacity-0');
          return;
        }

        // Stagger only the elements that entered together. Position-based
        // data-delay from Liquid (0, 100, 200 … 1200) kept later cards hidden.
        element.dataset.delay = String(Math.min(index * 60, 180));
        this.animateElement(element);
      });
    }
  
    animateElement(element) {
      if (this.isDestroyed) return;

      const delay = Math.max(0, Math.min(2000, parseInt(element.dataset.delay) || 0)); // Clamp delay
      
      if (delay === 0) {
        this.applyAnimation(element);
      } else {
        const timeoutId = setTimeout(() => {
          this.timeouts.delete(timeoutId);
          if (!this.isDestroyed) {
            this.applyAnimation(element);
          }
        }, delay);
        this.timeouts.add(timeoutId);
      }
    }

    applyAnimation(element) {
      if (this.isDestroyed) return;

      // Use requestAnimationFrame for smoother animations
      requestAnimationFrame(() => {
        element.classList.remove('opacity-0');
        
        // Determine animation type based on element classes
        if (element.classList.contains('fade-in-on-scroll')) {
          element.classList.add('animate-fade-in');
        } else {
          element.classList.add('animate-fade-slide-up');
        }
        
        element.classList.add('scroll-animated');
      });
    }

    meetsBreakpoint(breakpoint) {
      // Define breakpoint values (matching Tailwind defaults)
      const breakpoints = {
        'sm': 640,
        'md': 768,
        'lg': 1024,
        'xl': 1280,
        '2xl': 1536
      };

      const breakpointValue = breakpoints[breakpoint];
      if (!breakpointValue) return true; // If breakpoint not found, allow animation

      return window.innerWidth >= breakpointValue;
    }
  
    handleReducedMotion() {
      const elements = document.querySelectorAll('.fade-slide-up-on-scroll, .fade-in-on-scroll, [data-scroll-animation="fade-slide-up"], [data-scroll-animation="fade-in"]');
      elements.forEach(element => {
        // Check if element has responsive breakpoint requirement
        const responsiveBreakpoint = element.dataset.responsive;
        if (responsiveBreakpoint && !this.meetsBreakpoint(responsiveBreakpoint)) {
          // If breakpoint requirement not met, show element immediately
          element.classList.remove('opacity-0');
          return;
        }
        
        element.classList.remove('opacity-0');
        element.classList.add('scroll-animated');
      });
    }

    handleUnsupportedBrowser() {
      // Fallback for browsers without IntersectionObserver
      console.warn('ScrollFadeAnimations: IntersectionObserver not supported, showing all elements');
      this.handleReducedMotion();
    }

    destroy() {
      if (this.isDestroyed) return;
      
      this.isDestroyed = true;

      // Clear all timeouts
      this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
      this.timeouts.clear();

      // Disconnect observer
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      // Clear sets
      this.animatedElements.clear();

      // Remove event listeners
      window.removeEventListener('pagehide', this.handlePageHide);
      window.removeEventListener('beforeunload', this.handlePageHide);
      window.removeEventListener('resize', this.handleResize);
      
      // Clear resize timeout
      clearTimeout(this.resizeTimeout);
    }
  }
  
  // Initialize only if not already initialized
  if (!window.scrollFadeAnimationsInstance) {
    window.scrollFadeAnimationsInstance = new ScrollFadeAnimations();
  }